/**
 * Theme-Aware Favicon Switcher for AmalQ
 *
 * This utility automatically switches favicons based on the user's system theme preference.
 * It uses the `prefers-color-scheme` media query to detect light/dark mode and updates
 * all favicon references accordingly.
 *
 * Features:
 * - Automatic theme detection
 * - Real-time theme change listening
 * - Fallback support for older browsers
 * - Performance optimized with minimal DOM queries
 *
 * @author Claude Assistant
 * @version 1.0.0
 * @since 2025-09-03
 */

interface FaviconConfig {
  light: string;
  dark: string;
}

class FaviconThemeSwitcher {
  private themeMediaQuery: MediaQueryList;
  // Temporarily disabled black/dark theme favicons - using white/light theme only
  // TODO: Re-enable when better black favicon designs are available
  private faviconConfigs: Record<string, FaviconConfig> = {
    'link[rel="icon"]': {
      light: '/logo/favicon-white/favicon.ico',
      dark: '/logo/favicon-white/favicon.ico' // Temporarily using white for both themes
    },
    'link[rel="apple-touch-icon"]': {
      light: '/logo/favicon-white/apple-touch-icon.png',
      dark: '/logo/favicon-white/apple-touch-icon.png' // Temporarily using white for both themes
    },
    'link[rel="manifest"]': {
      light: '/logo/favicon-white/site.webmanifest',
      dark: '/logo/favicon-white/site.webmanifest' // Temporarily using white for both themes
    },
    'link[sizes="32x32"]': {
      light: '/logo/favicon-white/favicon-32x32.png',
      dark: '/logo/favicon-white/favicon-32x32.png' // Temporarily using white for both themes
    },
    'link[sizes="16x16"]': {
      light: '/logo/favicon-white/favicon-16x16.png',
      dark: '/logo/favicon-white/favicon-16x16.png' // Temporarily using white for both themes
    },
    'link[sizes="192x192"]': {
      light: '/logo/favicon-white/android-chrome-192x192.png',
      dark: '/logo/favicon-white/android-chrome-192x192.png' // Temporarily using white for both themes
    },
    'link[sizes="512x512"]': {
      light: '/logo/favicon-white/android-chrome-512x512.png',
      dark: '/logo/favicon-white/android-chrome-512x512.png' // Temporarily using white for both themes
    }
  };

  constructor() {
    // Initialize theme media query
    this.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Bind methods
    this.updateFavicons = this.updateFavicons.bind(this);
    this.handleThemeChange = this.handleThemeChange.bind(this);

    // Initialize favicons on load
    this.updateFavicons();

    // Listen for theme changes
    this.themeMediaQuery.addEventListener('change', this.handleThemeChange);

    // Listen for page visibility changes (user might change theme while page is hidden)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateFavicons();
      }
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.themeMediaQuery.removeEventListener('change', this.handleThemeChange);
    });
  }

  /**
   * Get current theme preference
   */
  private getCurrentTheme(): 'light' | 'dark' {
    return this.themeMediaQuery.matches ? 'dark' : 'light';
  }

  /**
   * Update all favicon links based on current theme
   */
  private updateFavicons(): void {
    const theme = this.getCurrentTheme();

    // Update each favicon type
    Object.entries(this.faviconConfigs).forEach(([selector, config]) => {
      const element = document.querySelector(selector) as HTMLLinkElement;
      if (element) {
        const newHref = config[theme];
        if (element.href !== newHref) {
          element.href = newHref;
        }
      }
    });

    // Dispatch custom event for other parts of the app
    const themeChangeEvent = new CustomEvent('faviconThemeChanged', {
      detail: { theme }
    });
    document.dispatchEvent(themeChangeEvent);
  }

  /**
   * Handle theme change events
   */
  private handleThemeChange(_event: MediaQueryListEvent): void {
    // Debounce theme changes to prevent rapid switching
    setTimeout(() => {
      this.updateFavicons();
    }, 50);
  }

  /**
   * Manually trigger favicon update (useful for testing)
   */
  public forceUpdate(): void {
    this.updateFavicons();
  }

  /**
   * Get current favicon theme (temporarily always returns 'light')
   * TODO: Re-enable theme detection when better black designs are available
   */
  public getCurrentFaviconTheme(): 'light' | 'dark' {
    // Temporarily disabled theme detection - always using white/light favicons
    return 'light';
  }

  /**
   * Check if the browser supports theme detection
   */
  public static isThemeDetectionSupported(): boolean {
    return typeof window !== 'undefined' &&
           'matchMedia' in window &&
           window.matchMedia('(prefers-color-scheme: dark)').media !== 'not all';
  }
}

// Export singleton instance
let faviconSwitcher: FaviconThemeSwitcher | null = null;

/**
 * Initialize theme-aware favicon switching
 * Call this function when the DOM is ready
 */
export function initializeFaviconThemeSwitcher(): void {
  if (typeof window === 'undefined') {
    // Server-side rendering, skip initialization
    return;
  }

  if (!FaviconThemeSwitcher.isThemeDetectionSupported()) {
    console.warn('Theme-aware favicons not supported in this browser. Using default favicons.');
    return;
  }

  if (!faviconSwitcher) {
    faviconSwitcher = new FaviconThemeSwitcher();
  }
}

/**
 * Get current favicon theme (temporarily always returns 'light')
 * TODO: Re-enable theme detection when better black designs are available
 */
export function getCurrentFaviconTheme(): 'light' | 'dark' {
  // Temporarily disabled theme detection - always using white/light favicons
  return 'light';
}

/**
 * Force favicon update (for testing)
 */
export function forceFaviconUpdate(): void {
  faviconSwitcher?.forceUpdate();
}

// Auto-initialize if script is loaded directly
if (typeof window !== 'undefined' && document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFaviconThemeSwitcher);
} else if (typeof window !== 'undefined') {
  initializeFaviconThemeSwitcher();
}
